/**
 * ChildWeekView — "Uka mi"
 * Shows the next 7 days of calendar events in a child-friendly format.
 * Used inside ChildDayView for both child devices and parent preview.
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { getDateString } from "@/lib/family-date";
import type { DeviceHashes } from "@/hooks/useDeviceHashes";

// ── Type config ─────────────────────────────────────
const EVENT_TYPE_CONFIG: Record<string, { emoji: string; bg: string; text: string }> = {
  school:   { emoji: "📚", bg: "bg-blue-100",   text: "text-blue-700" },
  activity: { emoji: "⚽", bg: "bg-green-100",  text: "text-green-700" },
  deadline: { emoji: "⏰", bg: "bg-amber-100",  text: "text-amber-700" },
  notice:   { emoji: "🔔", bg: "bg-purple-100", text: "text-purple-700" },
};

const fallbackConfig = { emoji: "📌", bg: "bg-muted", text: "text-muted-foreground" };

function getConfig(eventType: string) {
  return EVENT_TYPE_CONFIG[eventType] ?? fallbackConfig;
}

// ── Norwegian day names ─────────────────────────────
const DAY_NAMES_SHORT = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"];
const DAY_NAMES_LONG  = ["Søndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"];

// ── Helpers ─────────────────────────────────────────
function formatTime(t: string | null) {
  if (!t) return null;
  // "HH:MM:SS" → "HH:MM"
  return t.slice(0, 5);
}

function getNext7Days(todayStr: string): Date[] {
  const base = new Date(todayStr + "T12:00:00");
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    return d;
  });
}

interface CalendarEventRow {
  id: string;
  event_type: string;
  title: string;
  description: string | null;
  start_time: string | null;
  end_time: string | null;
  metadata: Record<string, any> | null;
  specific_date: string | null;
  day_of_week: number | null;
  recurring: boolean;
}

// ── Props ───────────────────────────────────────────
interface ChildWeekViewProps {
  childId: string;
  deviceHashes: DeviceHashes | null;
  timezone: string;
  today: string; // YYYY-MM-DD
  isParentPreview: boolean;
}

const MAX_VISIBLE = 4;

type DisplayItem = {
  kind: "single";
  event: CalendarEventRow;
} | {
  kind: "group";
  eventType: string;
  events: CalendarEventRow[];
};

function groupEvents(events: CalendarEventRow[]): DisplayItem[] {
  if (events.length === 0) return [];

  // Count by type to decide what to group
  const typeCounts: Record<string, number> = {};
  for (const ev of events) {
    typeCounts[ev.event_type] = (typeCounts[ev.event_type] ?? 0) + 1;
  }

  // Group any type that has 3+ events on the same day
  const groupableTypes = new Set(
    Object.entries(typeCounts)
      .filter(([, count]) => count >= 3)
      .map(([type]) => type)
  );

  if (groupableTypes.size === 0) {
    return events.map(ev => ({ kind: "single" as const, event: ev }));
  }

  const items: DisplayItem[] = [];
  let pendingGroup: CalendarEventRow[] = [];
  let pendingType: string | null = null;

  const flushGroup = () => {
    if (pendingGroup.length > 0 && pendingType) {
      items.push({ kind: "group", eventType: pendingType, events: pendingGroup });
      pendingGroup = [];
      pendingType = null;
    }
  };

  for (const ev of events) {
    if (groupableTypes.has(ev.event_type)) {
      if (pendingType === ev.event_type) {
        pendingGroup.push(ev);
      } else {
        flushGroup();
        pendingType = ev.event_type;
        pendingGroup = [ev];
      }
    } else {
      flushGroup();
      items.push({ kind: "single", event: ev });
    }
  }
  flushGroup();

  return items;
}

function formatTimeRange(events: CalendarEventRow[]): string | null {
  const starts = events.map(e => e.start_time).filter(Boolean).sort();
  const ends = events.map(e => e.end_time).filter(Boolean).sort();
  if (starts.length === 0) return null;
  const first = formatTime(starts[0]!);
  const last = ends.length > 0 ? formatTime(ends[ends.length - 1]!) : null;
  if (first && last) return `${first} – ${last}`;
  return first;
}

export function ChildWeekView({ childId, deviceHashes, timezone, today, isParentPreview }: ChildWeekViewProps) {
  const days = useMemo(() => getNext7Days(today), [today]);
  const startDate = getDateString(days[0], timezone);
  const endDate = getDateString(days[6], timezone);

  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Fetch events for range ──────────────────────
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["child-week-events", childId, startDate, endDate, isParentPreview ? "preview" : "device"],
    queryFn: async () => {
      if (isParentPreview) {
        // Parent preview: direct query via RLS
        const dayOfWeeks = days.map(d => (d.getDay() + 6) % 7); // ISO Mon=0..Sun=6
        const dateStrs = days.map(d => getDateString(d, timezone));

        const { data, error } = await supabase
          .from("child_calendar_events")
          .select("id, event_type, title, description, start_time, end_time, metadata, specific_date, day_of_week, recurring")
          .eq("child_id", childId)
          .or(
            `and(recurring.eq.true,day_of_week.in.(${dayOfWeeks.join(",")}),or(valid_from.is.null,valid_from.lte.${endDate}),or(valid_until.is.null,valid_until.gte.${startDate})),and(recurring.eq.false,specific_date.gte.${startDate},specific_date.lte.${endDate})`
          )
          .order("start_time", { ascending: true, nullsFirst: false });
        if (error) throw error;
        return (data ?? []) as unknown as CalendarEventRow[];
      }

      if (!deviceHashes) return [];
      const { data, error } = await supabase.rpc("get_calendar_events_for_device_range" as any, {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _start_date: startDate,
        _end_date: endDate,
      });
      if (error) throw error;
      return (data ?? []) as unknown as CalendarEventRow[];
    },
    enabled: !!childId && (isParentPreview || !!deviceHashes),
  });

  // ── Group events by day ─────────────────────────
  const eventsByDay = useMemo(() => {
    const map: Record<number, CalendarEventRow[]> = {};
    days.forEach((_, i) => { map[i] = []; });

    for (const ev of events) {
      if (ev.recurring && ev.day_of_week != null) {
        // Match to each day with the same ISO weekday
        days.forEach((d, i) => {
          const isoDow = (d.getDay() + 6) % 7;
          if (isoDow === ev.day_of_week) {
            map[i].push(ev);
          }
        });
      } else if (!ev.recurring && ev.specific_date) {
        days.forEach((d, i) => {
          if (getDateString(d, timezone) === ev.specific_date) {
            map[i].push(ev);
          }
        });
      }
    }
    return map;
  }, [events, days, timezone]);

  // Auto-scroll selected pill into view
  useEffect(() => {
    if (scrollRef.current) {
      const pill = scrollRef.current.children[selectedDayIdx] as HTMLElement | undefined;
      pill?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [selectedDayIdx]);

  // Check if any day has events
  const hasAnyEvents = Object.values(eventsByDay).some(arr => arr.length > 0);

  if (!hasAnyEvents && !isLoading) return null; // Don't show section if no events

  const getDayLabel = (idx: number) => {
    if (idx === 0) return "✨ I dag";
    if (idx === 1) return "👉 I morgen";
    return DAY_NAMES_LONG[days[idx].getDay()];
  };

  return (
    <div className="space-y-3 mt-4">
      {/* Header */}
      <h2 className="text-lg font-display font-bold px-1">🗓 Uka mi</h2>

      {/* Horizontal day pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide px-1" ref={scrollRef}>
        {days.map((d, i) => {
          const evCount = eventsByDay[i]?.length ?? 0;
          const isSelected = i === selectedDayIdx;
          const isToday = i === 0;
          return (
            <button
              key={i}
              onClick={() => setSelectedDayIdx(i)}
              className={`
                flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-xl text-sm font-semibold transition-all
                ${isSelected
                  ? "bg-primary text-primary-foreground shadow-md scale-105"
                  : isToday
                    ? "bg-primary/10 text-primary"
                    : "bg-card/60 text-muted-foreground"
                }
              `}
            >
              <span className="text-xs">{DAY_NAMES_SHORT[d.getDay()]}</span>
              <span className="text-base font-bold">{d.getDate()}</span>
              {evCount > 0 && (
                <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${isSelected ? "bg-primary-foreground" : "bg-primary"}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day events */}
      <div className="space-y-2 px-1">
        <h3 className="text-sm font-semibold text-muted-foreground">{getDayLabel(selectedDayIdx)}</h3>
        {isLoading ? (
          <Card className="border-0 bg-card/60 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-lg bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (eventsByDay[selectedDayIdx]?.length ?? 0) === 0 ? (
          <Card className="border-0 bg-card/60 shadow-sm rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground text-sm animate-bounce-in">
                Ingen planer denne dagen 🌈
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {(() => {
              const rawEvents = eventsByDay[selectedDayIdx] ?? [];
              const grouped = groupEvents(rawEvents);
              const visible = expandedDays.has(selectedDayIdx) ? grouped : grouped.slice(0, MAX_VISIBLE);

              return visible.map((item, idx) => {
                if (item.kind === "group") {
                  const cfg = getConfig(item.eventType);
                  const timeRange = formatTimeRange(item.events);
                  const isExpanded = expandedEvent === `group-${item.eventType}-${selectedDayIdx}`;
                  const groupId = `group-${item.eventType}-${selectedDayIdx}`;

                  return (
                    <button
                      key={groupId}
                      onClick={() => setExpandedEvent(isExpanded ? null : groupId)}
                      className="w-full text-left"
                    >
                      <Card className="border-0 bg-card/60 shadow-sm rounded-2xl transition-all hover:shadow-md active:scale-[0.98]">
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3">
                            <span className={`w-9 h-9 flex items-center justify-center rounded-xl text-lg ${cfg.bg}`}>
                              {cfg.emoji}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold text-sm ${cfg.text}`}>
                                {item.events.length} {item.eventType === "school" ? "skoletimer" : "hendelser"}
                              </p>
                              {timeRange && (
                                <p className="text-xs text-muted-foreground">{timeRange}</p>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {isExpanded ? "▲" : "▼"}
                            </span>
                          </div>
                          {isExpanded && (
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-1.5">
                              {item.events.map(ev => (
                                <div key={ev.id} className="flex items-center gap-2 text-xs">
                                  <span className="text-muted-foreground w-12 flex-shrink-0">
                                    {formatTime(ev.start_time) ?? "–"}
                                  </span>
                                  <span className={`font-medium ${cfg.text}`}>{ev.title}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </button>
                  );
                }

                // Single event
                const ev = item.event;
                const cfg = getConfig(ev.event_type);
                const isExpanded = expandedEvent === ev.id;
                const location = ev.metadata && typeof ev.metadata === "object" ? (ev.metadata as any).location : null;

                return (
                  <button
                    key={ev.id}
                    onClick={() => setExpandedEvent(isExpanded ? null : ev.id)}
                    className="w-full text-left"
                  >
                    <Card className="border-0 bg-card/60 shadow-sm rounded-2xl transition-all hover:shadow-md active:scale-[0.98]">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <span className={`w-9 h-9 flex items-center justify-center rounded-xl text-lg ${cfg.bg}`}>
                            {cfg.emoji}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-semibold text-sm truncate ${cfg.text}`}>{ev.title}</p>
                            {(ev.start_time || ev.end_time) && (
                              <p className="text-xs text-muted-foreground">
                                {formatTime(ev.start_time)}
                                {ev.start_time && ev.end_time ? " – " : ""}
                                {formatTime(ev.end_time)}
                              </p>
                            )}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                            {ev.description && (
                              <p className="text-xs text-muted-foreground">{ev.description}</p>
                            )}
                            {location && (
                              <p className="text-xs text-muted-foreground">📍 {location}</p>
                            )}
                            {!ev.description && !location && (
                              <p className="text-xs text-muted-foreground italic">Ingen mer info</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </button>
                );
              });
            })()}
            {(() => {
              const grouped = groupEvents(eventsByDay[selectedDayIdx] ?? []);
              return grouped.length > MAX_VISIBLE && !expandedDays.has(selectedDayIdx) ? (
                <button
                  onClick={() => setExpandedDays(prev => new Set(prev).add(selectedDayIdx))}
                  className="text-sm text-primary font-medium px-2 py-1"
                >
                  + {grouped.length - MAX_VISIBLE} ting til
                </button>
              ) : null;
            })()}
          </>
        )}
      </div>

      {/* Compact "later this week" preview (only when viewing today) */}
      {selectedDayIdx === 0 && (
        <div className="space-y-1 px-1">
          {days.slice(1, 7).map((d, rawIdx) => {
            const idx = rawIdx + 1;
            const dayEvents = eventsByDay[idx] ?? [];
            if (dayEvents.length === 0) return null;

            // Split into school vs other
            const schoolEvents = dayEvents.filter(ev => ev.event_type === "school");
            const otherEvents = dayEvents.filter(ev => ev.event_type !== "school");

            const schoolTimeRange = schoolEvents.length > 0 ? formatTimeRange(schoolEvents) : null;
            const schoolCfg = getConfig("school");

            return (
              <button
                key={idx}
                onClick={() => setSelectedDayIdx(idx)}
                className="w-full flex items-center gap-2 text-left py-1.5 px-2 rounded-xl hover:bg-muted/50 transition-colors"
              >
                <span className="text-xs text-muted-foreground w-12 flex-shrink-0 font-medium">
                  {idx === 1 ? "I morgen" : DAY_NAMES_SHORT[d.getDay()]}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex gap-1.5 flex-wrap items-center">
                    {schoolEvents.length > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${schoolCfg.bg} ${schoolCfg.text} font-medium`}>
                        {schoolCfg.emoji} Skole{schoolTimeRange ? ` · ${schoolTimeRange}` : ""}
                      </span>
                    )}
                    {otherEvents.slice(0, 2).map((ev) => {
                      const cfg = getConfig(ev.event_type);
                      return (
                        <span key={ev.id} className={`text-xs px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} font-medium truncate max-w-[120px]`}>
                          {cfg.emoji} {ev.title}
                        </span>
                      );
                    })}
                    {otherEvents.length > 2 && (
                      <span className="text-xs text-muted-foreground">+{otherEvents.length - 2}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
